import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { squadPlayerApi, playerApi, tournamentApi } from '../services/api';
import SeriesAutocomplete from '../components/SeriesAutocomplete';
import MatchAutocomplete from '../components/MatchAutocomplete';
import SquadDropdown from '../components/SquadDropdown';
import PlayerAutocomplete from '../components/PlayerAutocomplete';
import { SquadPlayer, Player, Series, Match, PaginatedResponse } from '../models';

interface FilterState {
  squad__series: Series | null;
  match: Match | null;
  squad: number | null;
  player: Player | null;
  ordering: string;
}

interface SquadPlayerFormValues {
  id: number;
  player: number | null;
  squad: any;
  role: string;
  status: string;
  credits: number;
  points: number;
  average_points: number;
  runs: number;
  wickets: number;
  dt_perc: number;
  matches: number;
}

const validationSchema = yup.object({
  player: yup.object().shape({
    id: yup.number().required('Player is required'),
  }).required('Player is required'),
  squad: yup.object().shape({
    id: yup.number().required('Squad is required'),
  }).required('Squad is required'),
  role: yup.string().required('Role is required'),
  status: yup.string().required('Status is required'),
  credits: yup.number(),
  points: yup.number(),
  average_points: yup.number(),
  runs: yup.number(),
  wickets: yup.number(),
  dt_perc: yup.number(),
  matches: yup.number(),
});

const roleOptions = [
  { value: 'BAT', label: 'Batsman' },
  { value: 'BOWL', label: 'Bowler' },
  { value: 'ALL', label: 'All-rounder' },
  { value: 'WK', label: 'Wicket-keeper' },
];

const statusOptions = [
  { value: 'PLAYING', label: 'Playing' },
  { value: 'NOT_PLAYING', label: 'Not Playing' },
  { value: 'INJURED', label: 'Injured' },
  { value: 'DOUBTFUL', label: 'Doubtful' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
];

const SquadPlayers: React.FC = () => {
  const [squadPlayers, setSquadPlayers] = useState<SquadPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedSquadPlayer, setSelectedSquadPlayer] = useState<SquadPlayer | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [filters, setFilters] = useState<FilterState>({
    squad__series: null,
    match: null,
    squad: null,
    player: null,
    ordering: '',
  });
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [availableSquads, setAvailableSquads] = useState<any[]>([]);

  // Check URL parameters when component mounts
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const seriesId = urlParams.get('series');
    const matchId = urlParams.get('match');
    const squadId = urlParams.get('squad');
    const playerId = urlParams.get('player');
    
    const fetchInitialFilters = async () => {
      try {
        setLoading(true);
        const filtersToUpdate = { ...filters };
        
        // If series ID is in the URL, fetch the series details
        if (seriesId) {
          try {
            const response = await tournamentApi.getById(Number(seriesId));
            if (response.data) {
              filtersToUpdate.squad__series = response.data;
            }
          } catch (error) {
            console.error('Error fetching series details:', error);
          }
        }
        
        // If match ID is in the URL, fetch the match details
        if (matchId) {
          try {
            const { matchApi } = await import('../services/api');
            const response = await matchApi.getById(Number(matchId));
            if (response.data) {
              filtersToUpdate.match = response.data;
            }
          } catch (error) {
            console.error('Error fetching match details:', error);
          }
        }
        
        // If squad ID is in the URL, set it in filters
        if (squadId) {
          filtersToUpdate.squad = Number(squadId);
        }
        
        // If player ID is in the URL, fetch the player details
        if (playerId) {
          try {
            const response = await playerApi.getById(Number(playerId));
            if (response.data) {
              filtersToUpdate.player = response.data;
            }
          } catch (error) {
            console.error('Error fetching player details:', error);
          }
        }
        
        setFilters(filtersToUpdate);
      } catch (error) {
        console.error('Error fetching initial filters:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (seriesId || matchId || squadId || playerId) {
      fetchInitialFilters();
    }

    // Load players for dropdown
    const fetchPlayers = async () => {
      try {
        const response = await playerApi.getAll();
        if (response.data && response.data.results) {
          setAvailablePlayers(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };
    fetchPlayers();

    // For squads we would need an endpoint, using dummy data for now
    setAvailableSquads([
      { id: 1, name: 'India Squad', series_id: 1, team_name: 'India' },
      { id: 2, name: 'Australia Squad', series_id: 1, team_name: 'Australia' },
      { id: 3, name: 'England Squad', series_id: 2, team_name: 'England' },
      { id: 4, name: 'South Africa Squad', series_id: 2, team_name: 'South Africa' },
    ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: GridColDef[] = [
    // { 
    //   field: 'id', 
    //   headerName: 'ID', 
    //   width: 70, 
    //   sortable: true,
    //   filterable: true,
    //   type: 'string'
    // },
    { 
      field: 'player',
      headerName: 'Player',
      width: 150,
      sortable: true,
      valueGetter: (player: any) => player?.name || '',
    },
    { 
      field: 'squad',
      headerName: 'Squad',
      width: 250,
      sortable: true,
      valueGetter: (squad: any) => {
        return squad ? `${squad.team?.name || ''} - ${squad.series?.name || ''}` : '';
      }
    },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 150, 
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: roleOptions.map(opt => opt.value),
      renderCell: (params: any) => {
        const role = roleOptions.find(r => r.value === params.value);
        return role ? role.label : params.value;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: statusOptions.map(opt => opt.value),
      renderCell: (params) => {
        const status = statusOptions.find(s => s.value === params.value);
        const statusLabel = status ? status.label : params.value;
        
        return (
          <Box
            sx={{
              backgroundColor:
                params.value === 'PLAYING' || params.value === 'LAST_PLAYED'
                  ? '#4caf50'
                  : params.value === 'NOT_PLAYING'
                  ? '#f44336'
                  : params.value === 'USED_X_FACTOR_SUBSTITUTE'
                  ? '#ff9800'
                  : params.value === 'X_FACTOR_SUBSTITUTE'
                  ? '#ffeb3b'
                  : '#9e9e9e',
              color: 'white',
              padding: '0px 6px',
              fontSize: '0.75rem',
              borderRadius: '10px',
              textTransform: 'capitalize',
              display: 'inline-block',
              minWidth: '60px',
              textAlign: 'center',
            }}
          >
            {statusLabel}
          </Box>
        );
      },
    },
    { 
      field: 'credits', 
      headerName: 'C', 
      width: 20,
      sortable: true,
      type: 'number',
    },
    { 
      field: 'matches', 
      headerName: 'M', 
      width: 20, 
      sortable: true,
      type: 'number',
    },
    { 
      field: 'average_points', 
      headerName: 'A Pt', 
      width: 30, 
      sortable: true,
      type: 'number',
    },
    { 
      field: 'runs', 
      headerName: 'R', 
      width: 10, 
      sortable: true,
      type: 'number',
    },
    { 
      field: 'wickets', 
      headerName: 'W', 
      width: 10, 
      sortable: true,
      type: 'number',
    },
    { 
      field: 'dt_perc', 
      headerName: 'DT%', 
      width: 30, 
      sortable: true,
      type: 'number',
      valueFormatter: (params) => {
        if (params != null) {
          return `${params}%`;
        }
        return '';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh Player Stats">
            <IconButton
              color="info"
              size="small"
              onClick={() => handleRefreshPlayerStats(params.row.player.id)}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const formik = useFormik<SquadPlayerFormValues>({
    initialValues: {
      id: 0,
      player: null,
      squad: {},
      role: 'BAT',
      status: 'NOT_PLAYING',
      credits: 0,
      points: 0,
      average_points: 0,
      runs: 0,
      wickets: 0,
      dt_perc: 0,
      matches: 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedSquadPlayer) {
          await squadPlayerApi.update(selectedSquadPlayer.id, values);
        } else {
          await squadPlayerApi.create(values);
        }
        fetchSquadPlayers(page, pageSize);
        handleClose();
      } catch (error) {
        console.error('Error saving squad player:', error);
      }
    },
  });

  const fetchSquadPlayers = React.useCallback(async (pageNumber: number = 1, size: number = pageSize) => {
    try {
      setLoading(true);
      const params: any = {
        page: pageNumber,
        page_size: size,
      };

      // Apply server-side filters
      if (filters.squad__series) {
        params.squad__series = filters.squad__series.id;
      }
      if (filters.squad) {
        params.squad = filters.squad;
      }
      if (filters.player) {
        params.player = filters.player.id;
      }

      const response = await squadPlayerApi.getAll(params);
      const data = response.data as PaginatedResponse<SquadPlayer>;
      setSquadPlayers(data.results);
      // setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching squad players:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.squad__series, filters.squad, filters.player, pageSize]);



  useEffect(() => {
    fetchSquadPlayers(page, pageSize);
  }, [page, pageSize, filters, fetchSquadPlayers]);

  // const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
  //   setPage(value);
  // };

  const handleFilterChange = (name: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1); // Reset to first page when filters change
    
    // Update URL with query parameters for deep linking
    const searchParams = new URLSearchParams(window.location.search);
    
    if (name === 'squad__series' && value) {
      searchParams.set('series', value.id.toString());
    } else if (name === 'squad__series' && !value) {
      searchParams.delete('series');
    }
    
    if (name === 'match' && value) {
      searchParams.set('match', value.id.toString());
    } else if (name === 'match' && !value) {
      searchParams.delete('match');
    }
    
    if (name === 'squad' && value) {
      searchParams.set('squad', value.toString());
    } else if (name === 'squad' && !value) {
      searchParams.delete('squad');
    }
    
    if (name === 'player' && value) {
      searchParams.set('player', value.id.toString());
    } else if (name === 'player' && !value) {
      searchParams.delete('player');
    }
    
    const newUrl = window.location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.history.pushState({}, '', newUrl);
  };

  const handleEdit = (squadPlayer: SquadPlayer) => {
    setSelectedSquadPlayer(squadPlayer);
    formik.setValues({
      id: squadPlayer.id,
      player: squadPlayer.player,
      squad: squadPlayer.squad,
      role: squadPlayer.role || 'BAT',
      status: squadPlayer.status || 'NOT_PLAYING',
      credits: squadPlayer.credits || 0,
      points: squadPlayer.points || 0,
      average_points: squadPlayer.average_points || 0,
      runs: squadPlayer.runs || 0,
      wickets: squadPlayer.wickets || 0,
      dt_perc: squadPlayer.dt_perc || 0,
      matches: squadPlayer.matches || 0,
    });
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this squad player?')) {
      try {
        await squadPlayerApi.delete(id);
        fetchSquadPlayers(page, pageSize);
      } catch (error) {
        console.error('Error deleting squad player:', error);
      }
    }
  };

  const handleRefreshPlayerStats = async (playerId: number) => {
    try {
      await playerApi.refreshStats(playerId);
      // Show success message
      alert('Player stats refresh initiated. This may take a few moments.');
    } catch (error) {
      console.error('Error refreshing player stats:', error);
      alert('Failed to refresh player stats. Please try again later.');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedSquadPlayer(null);
    formik.resetForm();
  };

  const handleRefresh = () => {
    fetchSquadPlayers(page, pageSize);
  };

  return (
    <Box  sx={{ height: "calc(100vh - 240px)", width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Squad Players</Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              color="primary" 
              onClick={handleRefresh} 
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            Add Squad Player
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <SeriesAutocomplete
              value={filters.squad__series}
              onChange={(value) => handleFilterChange('squad__series', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MatchAutocomplete
              value={filters.match}
              seriesId={filters.squad__series?.id}
              onChange={(value) => handleFilterChange('match', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SquadDropdown
              value={filters.squad}
              seriesId={filters.squad__series?.id}
              matchId={filters.match?.id}
              onChange={(value) => handleFilterChange('squad', value)}
              disabled={!filters.squad__series && !filters.match}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <PlayerAutocomplete
              value={filters.player}
              onChange={(value) => handleFilterChange('player', value)}
              matchId={filters.match?.id}
            />
          </Grid>
          <Grid item xs={12} container justifyContent="flex-end">
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => {
                setFilters({
                  squad__series: null,
                  match: null,
                  squad: null,
                  player: null,
                  ordering: '',
                });
                // Update URL to remove query parameters
                window.history.pushState({}, '', window.location.pathname);
              }}
              sx={{ mt: 1 }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <DataGrid
        rows={squadPlayers}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50, page: 0 },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        sortingMode="client"
        filterMode="client"
        loading={loading}
        density="compact"
      />

      {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={Math.ceil(totalCount / pageSize)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box> */}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {selectedSquadPlayer ? 'Edit Squad Player' : 'Add Squad Player'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="player.id"
                  label="Player"
                  value={formik.values.player || ''}
                  onChange={(e) => {
                    const selectedPlayer = availablePlayers.find(p => p.id === Number(e.target.value));
                    formik.setFieldValue('player', selectedPlayer || {});
                  }}
                  error={formik.touched.player && Boolean(formik.errors.player)}
                  helperText={formik.touched.player && formik.errors.player}
                >
                  {availablePlayers.map((player) => (
                    <MenuItem key={player.id} value={player.id}>
                      {player.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="squad.id"
                  label="Squad"
                  value={formik.values.squad.id || ''}
                  onChange={(e) => {
                    const selectedSquad = availableSquads.find(s => s.id === Number(e.target.value));
                    formik.setFieldValue('squad', selectedSquad || {});
                  }}
                  error={formik.touched.squad && Boolean(formik.errors.squad)}
                  // helperText={formik.touched.squad && formik.errors.squad}
                >
                  {availableSquads.map((squad) => (
                    <MenuItem key={squad.id} value={squad.id}>
                      {squad.team_name} - {squad.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="role"
                  label="Role"
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  error={formik.touched.role && Boolean(formik.errors.role)}
                  helperText={formik.touched.role && formik.errors.role}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="status"
                  label="Status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  error={formik.touched.status && Boolean(formik.errors.status)}
                  helperText={formik.touched.status && formik.errors.status}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="credits"
                  label="Credits"
                  type="number"
                  value={formik.values.credits}
                  onChange={formik.handleChange}
                  error={formik.touched.credits && Boolean(formik.errors.credits)}
                  helperText={formik.touched.credits && formik.errors.credits}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="matches"
                  label="Matches"
                  type="number"
                  value={formik.values.matches}
                  onChange={formik.handleChange}
                  error={formik.touched.matches && Boolean(formik.errors.matches)}
                  helperText={formik.touched.matches && formik.errors.matches}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="average_points"
                  label="Average Points"
                  type="number"
                  value={formik.values.average_points}
                  onChange={formik.handleChange}
                  error={formik.touched.average_points && Boolean(formik.errors.average_points)}
                  helperText={formik.touched.average_points && formik.errors.average_points}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="runs"
                  label="Runs"
                  type="number"
                  value={formik.values.runs}
                  onChange={formik.handleChange}
                  error={formik.touched.runs && Boolean(formik.errors.runs)}
                  helperText={formik.touched.runs && formik.errors.runs}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="wickets"
                  label="Wickets"
                  type="number"
                  value={formik.values.wickets}
                  onChange={formik.handleChange}
                  error={formik.touched.wickets && Boolean(formik.errors.wickets)}
                  helperText={formik.touched.wickets && formik.errors.wickets}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="dt_perc"
                  label="Dream Team %"
                  type="number"
                  value={formik.values.dt_perc}
                  onChange={formik.handleChange}
                  error={formik.touched.dt_perc && Boolean(formik.errors.dt_perc)}
                  helperText={formik.touched.dt_perc && formik.errors.dt_perc}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedSquadPlayer ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SquadPlayers;
